import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { formatSectorDisplay, FACILITY_SECTOR_LABELS } from '../facilitySectors';
import {
    Box,
    Paper,
    Typography,
    CircularProgress,
    Chip,
    Stack,
    Button,
    IconButton,
    useMediaQuery,
    Pagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    Select,
    MenuItem,
    InputLabel,
    Avatar,
    AvatarGroup,
    Tooltip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import AddCommentIcon from '@mui/icons-material/AddComment';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreIcon from '@mui/icons-material/Restore';
import EventsApi, { ApprovalStatus, ReportWithApprovalsDto, DeletedReportDto } from '../../../api/eventsApi/eventsApi';
import { reportUi } from '../reportUiTheme';
import { PieChart, TrendSparkline } from './charts';
import FormattedLlmText from './FormattedLlmText';

interface WikipediaData {
    title: string | null;
    summary: string | null;
    thumbnail: string | null;
}

type PinEventItem = {
    id: string;
    date: string;
    eventType: string;
    description: string;
    district1?: string;
};

const APPROVAL_NOTE_LIMIT = 1000;

const FACILITY_NAME_MAX_LEN = 200;

const ROLE_CLAIMS_SUPERADMIN = '00000000-0000-0000-0000-000000000001';
const ROLE_CLAIMS_ADMIN = '00000000-0000-0000-0000-000000000002';

const PRESET_RADII = [5000, 10000, 15000] as const;

function formatRadiusHeader(meters: number): string {
    if (!Number.isFinite(meters) || meters <= 0) return '';
    if (meters >= 1000) {
        const km = meters / 1000;
        return km % 1 === 0 ? `${km} km` : `${km.toFixed(1)} km`;
    }
    return `${Math.round(meters)} m`;
}

const ApprovalDialog = ({
    open,
    fileId,
    status,
    onClose,
    onSubmit
}: {
    open: boolean;
    fileId: string;
    status: ApprovalStatus;
    onClose: () => void;
    onSubmit: (fileId: string, status: ApprovalStatus, note: string) => Promise<void>;
}) => {
    const [note, setNote] = useState('');

    useEffect(() => {
        if (open) {
            setNote('');
        }
    }, [open]);

    const isRejected = status === ApprovalStatus.Rejected;
    const isError = isRejected && !note.trim();

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: reportUi.radius, bgcolor: reportUi.pageBg, border: `1px solid ${reportUi.border}` } }}>
            <DialogTitle sx={{ bgcolor: reportUi.headerBg, color: '#fff' }}>
                {status === ApprovalStatus.Approved ? 'Raporu Onayla' : 'Raporu Reddet ve Not Ekle'}
            </DialogTitle>
            <DialogContent dividers sx={{ bgcolor: reportUi.pageBg }}>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    {status === ApprovalStatus.Approved 
                        ? 'Bu raporu onaylamak üzeresiniz. Eklemek istediğiniz bir not varsa aşağıya yazabilirsiniz.' 
                        : 'Bu raporu reddediyorsunuz. Lütfen reddetme sebebinizi veya düzeltme notunuzu belirtin (Zorunlu).'}
                </Typography>
                <TextField
                    autoFocus
                    margin="dense"
                    label="Not / Açıklama"
                    type="text"
                    fullWidth
                    multiline
                    rows={4}
                    variant="outlined"
                    value={note}
                    onChange={(e) => { if (e.target.value.length <= APPROVAL_NOTE_LIMIT) setNote(e.target.value); }}
                    inputProps={{ maxLength: APPROVAL_NOTE_LIMIT }}
                    required={isRejected}
                    error={isError}
                    helperText={
                        isError 
                            ? "Red sebebini belirtmek zorunludur" 
                            : `${note.length}/${APPROVAL_NOTE_LIMIT} karakter`
                    }
                />
            </DialogContent>
            <DialogActions sx={{ p: 2, gap: 1 }}>
                <Button 
                    onClick={onClose} 
                    variant="contained"
                    sx={{ 
                        fontWeight: 600, 
                        backgroundColor: `${reportUi.surface} !important`,
                        color: `${reportUi.textMain} !important`,
                        border: `1px solid ${reportUi.border}`,
                        borderRadius: reportUi.radius,
                        '&:hover': { backgroundColor: `${reportUi.surfaceSoft} !important` },
                        minWidth: 90,
                        fontSize: 14,
                        boxShadow: 'none',
                    }}
                >
                    İptal
                </Button>
                <Button 
                    onClick={() => onSubmit(fileId, status, note)} 
                    variant="contained" 
                    disabled={isError}
                    sx={{ 
                        fontWeight: 700,
                        minWidth: 90,
                        fontSize: 14,
                        backgroundColor: (status === ApprovalStatus.Approved ? '#1FA35C' : '#D93B2B') + ' !important',
                        color: '#fff !important',
                        '&:hover': { backgroundColor: (status === ApprovalStatus.Approved ? '#178A4C' : '#B82020') + ' !important' },
                        '&.Mui-disabled': { backgroundColor: '#ccc !important', color: '#888 !important' },
                        boxShadow: 'none',
                    }}
                >
                    Gönder
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const ReportPages = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isTablet = useMediaQuery("(min-width:600px) and (max-width:1199.95px)");
    const isMobile = useMediaQuery("(max-width:599.95px)");
    const isDesktop = !isTablet && !isMobile;
    const [loading, setLoading] = useState(true);
    const [viewportHeight, setViewportHeight] = useState<number>(() =>
        typeof window !== 'undefined' ? window.innerHeight : 900
    );
    const [pinData, setPinData] = useState<any>(null);
    const [events, setEvents] = useState<PinEventItem[]>([]);
    const [eventsLoading, setEventsLoading] = useState(false);
    const [timeRange, setTimeRange] = useState<'1m' | '3m' | '1y'>('1y');
    const [eventsPage, setEventsPage] = useState(1);
    const [expandedEventId, setExpandedEventId] = useState<string | null>(null);
    const [selectedEventTypes, setSelectedEventTypes] = useState<Set<string>>(new Set());
    const [pdfFiles, setPdfFiles] = useState<ReportWithApprovalsDto[]>([]);
    const SAVED_REPORTS_PAGE_SIZE = 5;
    const [savedReportsPage, setSavedReportsPage] = useState(1);
    type SavedReportSortKey = 'created' | 'updated';
    const [savedReportsSortBy, setSavedReportsSortBy] = useState<SavedReportSortKey>('created');
    const [savedReportsSortOrder, setSavedReportsSortOrder] = useState<'asc' | 'desc'>('desc');
    const [approvalDialogState, setApprovalDialogState] = useState<{ open: boolean, fileId: string, status: ApprovalStatus }>({
        open: false, fileId: '', status: ApprovalStatus.Pending
    });
    const [ownerWarningOpen, setOwnerWarningOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [reportToDelete, setReportToDelete] = useState<{ id: string; fileName: string } | null>(null);
    const [isSuperadmin, setIsSuperadmin] = useState(false);
    const [canEditFacility, setCanEditFacility] = useState(false);
    const [editFacilityOpen, setEditFacilityOpen] = useState(false);
    const [editName, setEditName] = useState('');
    const [editSectorKey, setEditSectorKey] = useState('');
    const [editSectorOther, setEditSectorOther] = useState('');
    const [editFacilityType, setEditFacilityType] = useState('');
    const [editRadiusOption, setEditRadiusOption] = useState<string>('5000');
    const [editCustomRadius, setEditCustomRadius] = useState('');
    const [editEmployeeCount, setEditEmployeeCount] = useState('');
    const [editFormError, setEditFormError] = useState<string | null>(null);
    const [isSavingFacility, setIsSavingFacility] = useState(false);
    const [deletedReports, setDeletedReports] = useState<DeletedReportDto[]>([]);
    const [deletedReportsLoading, setDeletedReportsLoading] = useState(false);
    const [hasActiveAudit, setHasActiveAudit] = useState(false);
    const [isResettingAudit, setIsResettingAudit] = useState(false);

    const getReportUpdatedMs = useCallback((f: ReportWithApprovalsDto) => {
        if (f.updatedDate) return new Date(f.updatedDate).getTime();
        const fromApprovals = f.approvals?.length
            ? Math.max(...f.approvals.map((a) => new Date(a.approvalDate).getTime()))
            : 0;
        return Math.max(new Date(f.createdDate).getTime(), fromApprovals);
    }, []);

    const handleSavedReportsSort = (key: SavedReportSortKey) => {
        if (savedReportsSortBy === key) {
            setSavedReportsSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
        } else {
            setSavedReportsSortBy(key);
            setSavedReportsSortOrder('desc');
        }
        setSavedReportsPage(1);
    };

    const pdfReportsSortedNewestFirst = useMemo(() => {
        const next = [...pdfFiles];
        next.sort((a, b) => {
            const ta = savedReportsSortBy === 'created'
                ? new Date(a.createdDate).getTime()
                : getReportUpdatedMs(a);
            const tb = savedReportsSortBy === 'created'
                ? new Date(b.createdDate).getTime()
                : getReportUpdatedMs(b);
            const cmp = ta - tb;
            return savedReportsSortOrder === 'asc' ? cmp : -cmp;
        });
        return next;
    }, [pdfFiles, savedReportsSortBy, savedReportsSortOrder, getReportUpdatedMs]);

    const reportVersionByFileId = useMemo(() => {
        const oldestFirst = [...pdfFiles].sort(
            (a, b) => new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime(),
        );
        const m = new Map<string, number>();
        oldestFirst.forEach((f, i) => { m.set(f.fileAttachmentId, i + 1); });
        return m;
    }, [pdfFiles]);

    const savedReportsPageCount = Math.max(1, Math.ceil(pdfReportsSortedNewestFirst.length / SAVED_REPORTS_PAGE_SIZE));

    const pagedSavedReports = useMemo(() => {
        const start = (savedReportsPage - 1) * SAVED_REPORTS_PAGE_SIZE;
        return pdfReportsSortedNewestFirst.slice(start, start + SAVED_REPORTS_PAGE_SIZE);
    }, [pdfReportsSortedNewestFirst, savedReportsPage]);

    const formatReportCreated = (iso: string) => {
        try {
            return new Date(iso).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' });
        } catch {
            return iso;
        }
    };

    useEffect(() => {
        setSavedReportsPage((p) => Math.min(Math.max(1, p), savedReportsPageCount));
    }, [savedReportsPageCount, pdfFiles.length]);
    const [currentUserId, setCurrentUserId] = useState<string | null>(null);
    const [wikipediaData, setWikipediaData] = useState<WikipediaData>({
        title: null,
        summary: null,
        thumbnail: null
    });
    const [generalOverview, setGeneralOverview] = useState('');
    const [isGeneralOverviewEditing, setIsGeneralOverviewEditing] = useState(false);
    const [isSavingGeneralOverview, setIsSavingGeneralOverview] = useState(false);
    const [isGeneratingGeneralOverview, setIsGeneratingGeneralOverview] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            if (!id) return;

            try {
                setLoading(true);
                EventsApi.clearCacheItem(`pin_info_${id}`);
                const pinInfo = await EventsApi.getPinInfo(id);

                const parseNumber = (v: any): number => {
                    if (typeof v === 'number') return v;
                    if (typeof v === 'string') {
                        const t = v.trim().replace(',', '.');
                        const n = Number(t);
                        return n;
                    }
                    return Number(v);
                };

                const rawLat = (pinInfo as any).latitude ?? (pinInfo as any).Latitude;
                const rawLng = (pinInfo as any).longitude ?? (pinInfo as any).Longitude;
                const rawRadius = (pinInfo as any).radiusInMeters ?? (pinInfo as any).RadiusInMeters ?? (pinInfo as any).radius;

                let latitude = parseNumber(rawLat);
                let longitude = parseNumber(rawLng);
                const radiusInMeters = parseNumber(rawRadius);

                // Lat/Lng swap guard
                const latValid = Number.isFinite(latitude) && latitude >= -90 && latitude <= 90;
                const lngValid = Number.isFinite(longitude) && longitude >= -180 && longitude <= 180;
                const latValidIfSwapped = Number.isFinite(longitude) && longitude >= -90 && longitude <= 90;
                const lngValidIfSwapped = Number.isFinite(latitude) && latitude >= -180 && latitude <= 180;
                if ((!latValid || !lngValid) && latValidIfSwapped && lngValidIfSwapped) {
                    const tmp = latitude;
                    latitude = longitude;
                    longitude = tmp;
                }

                const rawSector = (pinInfo as any).sector ?? (pinInfo as any).Sector ?? '';
                const rawSectorCustom =
                    (pinInfo as any).sectorCustom ?? (pinInfo as any).SectorCustom ?? null;
                const rawFacilityType =
                    (pinInfo as any).facilityType ?? (pinInfo as any).FacilityType ?? '';
                const rawEmployee =
                    (pinInfo as any).employeeCount ?? (pinInfo as any).EmployeeCount ?? null;
                let employeeCount: number | null = null;
                if (rawEmployee != null && rawEmployee !== '') {
                    const n = typeof rawEmployee === 'number' ? rawEmployee : parseInt(String(rawEmployee), 10);
                    if (Number.isFinite(n) && n >= 0) employeeCount = n;
                }
                const sectorKeyStr = typeof rawSector === 'string' ? rawSector.trim() : String(rawSector || '').trim();
                const pinWithRadius = {
                    id: (pinInfo as any).id ?? (pinInfo as any).Id,
                    name: (pinInfo as any).name ?? (pinInfo as any).Name,
                    sectorRaw: sectorKeyStr,
                    sectorCustom: typeof rawSectorCustom === 'string' ? rawSectorCustom : rawSectorCustom ?? null,
                    facilityType: typeof rawFacilityType === 'string' ? rawFacilityType.trim() : '',
                    employeeCount,
                    userId: (pinInfo as any).userId ?? (pinInfo as any).UserId,
                    latitude,
                    longitude,
                    radius: radiusInMeters
                };

                setPinData(pinWithRadius);
                let reportDescription =
                    (pinInfo as any).reportDescription
                    ?? (pinInfo as any).ReportDescription
                    ?? '';
                try {
                    const fullReport: any = await EventsApi.getPinFullReport(id);
                    reportDescription =
                        fullReport?.pinInfo?.reportDescription
                        ?? fullReport?.pinInfo?.ReportDescription
                        ?? reportDescription;
                } catch { /* ignore */ }
                setGeneralOverview(typeof reportDescription === 'string' ? reportDescription : '');

                const pinName = (pinInfo as any).name ?? (pinInfo as any).Name;
                let summary = (pinInfo as any).QuestionsJson?.wikipediaSummary ?? reportDescription ?? null;

                // Fallback: read "Sonuç ve Değerlendirme" question answer
                if (!summary) {
                    try {
                        const qaData = await EventsApi.getPinQuestionsAndAnswers(id);
                        const evalQ = qaData.questions.find((q: any) => q.text === 'Sonuç ve Değerlendirme');
                        const evalDesc = evalQ?.answers?.[0]?.description;
                        if (evalDesc && evalDesc !== 'gerek_yok' && evalDesc !== 'Gerek yok') {
                            summary = evalDesc;
                        }
                    } catch { /* ignore */ }
                }

                setWikipediaData({
                    title: pinName ?? null,
                    summary,
                    thumbnail: null
                });

                try {
                    const approvalsData = await EventsApi.getReportApprovalsByPin(id);
                    setPdfFiles(approvalsData);
                } catch (e) {
                    console.error("Error loading report approvals", e);
                }

                try {
                    const qaData = await EventsApi.getPinQuestionsAndAnswers(id);
                    const answeredCount = qaData.questions.filter((q: any) => q.answers?.length > 0).length;
                    setHasActiveAudit(answeredCount > 0);
                } catch { /* ignore */ }

            } catch (error) {
                console.error('Veri yuklenirken hata:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [id]);

    useEffect(() => {
        const onResize = () => setViewportHeight(window.innerHeight);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    useEffect(() => {
        const fetchCurrentUser = async () => {
            try {
                const user = await EventsApi.getUser();
                setCurrentUserId(user.id);
                const claims = user.operationClaimIds || [];
                const isSA = claims.includes(ROLE_CLAIMS_SUPERADMIN);
                setIsSuperadmin(isSA);
                setCanEditFacility(isSA || claims.includes(ROLE_CLAIMS_ADMIN));
            } catch { /* ignore */ }
        };
        fetchCurrentUser();
    }, []);

    const refreshAfterReportSaved = useCallback(async () => {
        if (!id) return;
        try {
            EventsApi.clearCacheItem(`pin_info_${id}`);
            const approvalsData = await EventsApi.getReportApprovalsByPin(id);
            setPdfFiles(approvalsData);
        } catch { /* ignore */ }
        setHasActiveAudit(false);
    }, [id]);

    useEffect(() => {
        if (!id) return;
        const key = `report_saved_${id}`;
        const onStorage = (e: StorageEvent) => {
            if (e.key === key) refreshAfterReportSaved();
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, [id, refreshAfterReportSaved]);

    const rangeDays = timeRange === '1m' ? 30 : timeRange === '3m' ? 90 : 365;

    useEffect(() => {
        const fetchEvents = async () => {
            if (!id) return;
            try {
                setEventsLoading(true);

                const end = new Date();
                const start = new Date();
                start.setDate(end.getDate() - rangeDays);

                const toISODate = (d: Date) => d.toISOString().slice(0, 10);
                const res = await EventsApi.getPinEvents(id, toISODate(start), toISODate(end));

                const flattened: PinEventItem[] = (res?.locations ?? []).flatMap((loc: any) =>
                    (loc.events ?? []).map((e: any) => ({
                        id: e.id ?? e.Id,
                        date: e.date ?? e.Date,
                        eventType: e.eventType ?? e.EventType,
                        description: e.description ?? e.Description,
                        district1: loc.district1 ?? loc.District1,
                    }))
                );

                flattened.sort((a, b) => {
                    const da = new Date(a.date).getTime();
                    const db = new Date(b.date).getTime();
                    return db - da;
                });

                setEvents(flattened);
            } catch (e) {
                console.error('Pin events fetch error:', e);
                setEvents([]);
            } finally {
                setEventsLoading(false);
            }
        };

        fetchEvents();
    }, [id, rangeDays]);

    useEffect(() => {
        setEventsPage(1);
        setExpandedEventId(null);
    }, [timeRange]);

    useEffect(() => {
        setEventsPage(1);
        setExpandedEventId(null);
    }, [selectedEventTypes]);

    const handleOpenChecklist = async () => {
        if (!id) return;
        navigate(`/reports/${id}/checklist`);
    };

    const handleBack = () => {
        navigate('/reports');
    };

    const handleDownloadPdf = async (fileId: string, fileName: string) => {
        try {
            const blob = await EventsApi.getFileAttachment(fileId);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (e) {
            console.error('PDF indirme hatasi:', e);
        }
    };

    const handleSaveGeneralOverview = async (nextText?: string) => {
        const text = (nextText ?? generalOverview);
        if (!id) return;
        setIsSavingGeneralOverview(true);
        try {
            const formData = new FormData();
            formData.append('Id', id);
            formData.append('ReportDescription', text);
            await EventsApi.updatePin(id, formData);
            EventsApi.clearCacheItem(`pin_info_${id}`);
            EventsApi.clearCacheItem(`pin_full_report_${id}`);
            EventsApi.clearCacheItem('pins');
            setGeneralOverview(text);
        } catch (e) {
            console.error('Genel bakış kaydetme hatası:', e);
        } finally {
            setIsSavingGeneralOverview(false);
        }
    };

    const loadDeletedReports = useCallback(async () => {
        if (!id || !isSuperadmin) return;
        setDeletedReportsLoading(true);
        try {
            const data = await EventsApi.getDeletedReports(id);
            setDeletedReports(data);
        } catch { /* ignore */ }
        finally { setDeletedReportsLoading(false); }
    }, [id, isSuperadmin]);

    useEffect(() => {
        loadDeletedReports();
    }, [loadDeletedReports]);

    const handleDeleteReport = (fileId: string, fileName: string) => {
        setReportToDelete({ id: fileId, fileName });
        setDeleteDialogOpen(true);
    };

    const handleConfirmDeleteReport = async () => {
        if (!reportToDelete) return;
        try {
            await EventsApi.softDeleteReport(reportToDelete.id);
            setPdfFiles((prev) => prev.filter((f) => f.fileAttachmentId !== reportToDelete.id));
            if (isSuperadmin) loadDeletedReports();
        } catch (e: any) {
            alert(e?.response?.data?.message || 'Rapor silinemedi.');
        } finally {
            setDeleteDialogOpen(false);
            setReportToDelete(null);
        }
    };

    const handleGenerateGeneralOverview = async () => {
        if (!id) return;
        setIsGeneratingGeneralOverview(true);
        try {
            const end = new Date();
            const start = new Date();
            start.setFullYear(end.getFullYear() - 1);
            const toISO = (d: Date) => d.toISOString().split('T')[0];
            const result: any = await EventsApi.getPinReport(id, toISO(start), toISO(end));
            const generated = (result?.reportDescription ?? '').toString();
            setGeneralOverview(generated);
            if (generated.trim() && currentUserId) {
                await handleSaveGeneralOverview(generated);
            }
        } catch (e) {
            console.error('UMAY genel bakış üretme hatası:', e);
        } finally {
            setIsGeneratingGeneralOverview(false);
        }
    };

    const facilityName = pinData?.name ?? wikipediaData.title ?? 'Tesis Detayları';
    const sectorLabel = pinData
        ? formatSectorDisplay(pinData.sectorRaw, pinData.sectorCustom) || '—'
        : '—';
    const facilityTypeLabel =
        pinData?.facilityType && String(pinData.facilityType).trim()
            ? String(pinData.facilityType).trim()
            : '—';
    const radiusLabel =
        pinData && pinData.radius != null && Number.isFinite(Number(pinData.radius))
            ? formatRadiusHeader(Number(pinData.radius))
            : null;
    const employeeCountLabel =
        pinData?.employeeCount != null && pinData.employeeCount >= 0
            ? String(pinData.employeeCount)
            : '—';

    const editRadiusMeters = useMemo(() => {
        if (editRadiusOption === 'custom') {
            const n = Number(editCustomRadius);
            return Number.isFinite(n) && n > 0 ? Math.round(n) : 0;
        }
        return Number(editRadiusOption) || 0;
    }, [editRadiusOption, editCustomRadius]);

    const openEditFacility = () => {
        if (!pinData) return;
        setEditName(String(pinData.name ?? wikipediaData.title ?? '').slice(0, FACILITY_NAME_MAX_LEN));
        const sr = (pinData.sectorRaw || '').trim();
        const sc = typeof pinData.sectorCustom === 'string' ? pinData.sectorCustom.trim() : '';
        if (sr && Object.prototype.hasOwnProperty.call(FACILITY_SECTOR_LABELS, sr)) {
            setEditSectorKey(sr);
            setEditSectorOther(sr === 'other' ? sc : '');
        } else if (sr || sc) {
            setEditSectorKey('other');
            setEditSectorOther(sc || sr);
        } else {
            setEditSectorKey('');
            setEditSectorOther('');
        }
        setEditFacilityType(pinData.facilityType || '');
        const r = Number(pinData.radius);
        if (Number.isFinite(r) && r > 0) {
            const rounded = Math.round(r);
            if ((PRESET_RADII as readonly number[]).includes(rounded)) {
                setEditRadiusOption(String(rounded));
                setEditCustomRadius('');
            } else {
                setEditRadiusOption('custom');
                setEditCustomRadius(String(rounded));
            }
        } else {
            setEditRadiusOption('5000');
            setEditCustomRadius('');
        }
        const ec = pinData.employeeCount;
        setEditEmployeeCount(ec != null && ec >= 0 ? String(ec) : '');
        setEditFormError(null);
        setEditFacilityOpen(true);
    };

    const handleEditRadiusOptionChange = (value: string) => {
        setEditRadiusOption(value);
        if (value === 'custom') {
            setEditCustomRadius((prev) => prev || '');
            return;
        }
        setEditCustomRadius('');
    };

    const handleSaveFacility = async () => {
        if (!id || !canEditFacility) return;
        setEditFormError(null);
        const nameTrim = editName.trim().slice(0, FACILITY_NAME_MAX_LEN);
        if (!nameTrim) {
            setEditFormError('Tesis adı zorunludur.');
            return;
        }
        if (!editSectorKey) {
            setEditFormError('Sektör seçin.');
            return;
        }
        if (editSectorKey === 'other' && !editSectorOther.trim()) {
            setEditFormError('“Diğer” seçildiğinde sektör adını yazın.');
            return;
        }
        const radiusMeters = editRadiusMeters;
        if (radiusMeters <= 0) {
            setEditFormError('Geçerli bir yarıçap (metre) seçin veya girin.');
            return;
        }
        setIsSavingFacility(true);
        try {
            const formData = new FormData();
            formData.append('Id', id);
            formData.append('Name', nameTrim);
            formData.append('RadiusInMeters', String(radiusMeters));
            formData.append('Sector', editSectorKey);
            formData.append('SectorCustom', editSectorKey === 'other' ? editSectorOther.trim() : '');
            formData.append('FacilityType', editFacilityType.trim());
            const emp = editEmployeeCount.trim() ? parseInt(editEmployeeCount.trim(), 10) : NaN;
            if (Number.isFinite(emp) && emp >= 0) {
                formData.append('EmployeeCount', String(emp));
            }
            await EventsApi.updatePin(id, formData);
            EventsApi.clearCacheItem(`pin_info_${id}`);
            EventsApi.clearCacheItem(`pin_full_report_${id}`);
            EventsApi.clearCacheItem('pins');
            const nextEmployee =
                Number.isFinite(emp) && emp >= 0 ? emp : pinData?.employeeCount ?? null;
            setPinData((prev: any) =>
                prev
                    ? {
                          ...prev,
                          name: nameTrim,
                          sectorRaw: editSectorKey,
                          sectorCustom: editSectorKey === 'other' ? editSectorOther.trim() : null,
                          facilityType: editFacilityType.trim(),
                          radius: radiusMeters,
                          employeeCount: nextEmployee,
                      }
                    : prev
            );
            setWikipediaData((prev) => ({ ...prev, title: nameTrim }));
            setEditFacilityOpen(false);
        } catch (e) {
            console.error('Tesis güncellenemedi:', e);
            alert('Tesis bilgileri kaydedilemedi. Yetkinizi veya bağlantınızı kontrol edin.');
        } finally {
            setIsSavingFacility(false);
        }
    };

    const totalEventCount = events.length;

    const uniqueEventTypes = useMemo(() => {
        const types = new Set<string>();
        for (const e of events) types.add((e.eventType || '').trim() || 'Diğer');
        return Array.from(types).sort();
    }, [events]);

    const filteredEvents = useMemo(() => {
        if (selectedEventTypes.size === 0) return events;
        return events.filter((e) => {
            const t = (e.eventType || '').trim() || 'Diğer';
            return selectedEventTypes.has(t);
        });
    }, [events, selectedEventTypes]);

    const filteredEventCount = filteredEvents.length;

    const itemsPerPage = useMemo(() => {
        if (isDesktop) return 7;
        const reservedHeight = isDesktop ? 360 : isMobile ? 430 : 400;
        const rowHeight = isDesktop ? 76 : 72;
        return Math.max(4, Math.floor((viewportHeight - reservedHeight) / rowHeight));
    }, [viewportHeight, isDesktop, isMobile]);
    const eventsPageCount = Math.max(1, Math.ceil(filteredEventCount / itemsPerPage));
    const recentEvents = filteredEvents.slice((eventsPage - 1) * itemsPerPage, eventsPage * itemsPerPage);

    const trendCounts = useMemo(() => {
        const today = new Date();
        const rangeEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const rangeStart = new Date(rangeEnd);
        rangeStart.setDate(rangeEnd.getDate() - (rangeDays - 1));

        if (rangeDays <= 30) {
            const dayKeys: string[] = Array.from({ length: rangeDays }, (_, i) => {
                const d = new Date(rangeStart);
                d.setDate(rangeStart.getDate() + i);
                return d.toISOString().slice(0, 10);
            });
            const map = new Map<string, number>();
            for (const key of dayKeys) map.set(key, 0);
            for (const e of events) {
                const key = String(e.date).slice(0, 10);
                if (map.has(key)) map.set(key, (map.get(key) ?? 0) + 1);
            }
            return dayKeys.map((k) => {
                const dateParts = k.split('-');
                return {
                    label: `${dateParts[2]}/${dateParts[1]}`, // DD/MM format
                    value: map.get(k) ?? 0
                };
            });
        }

        const bucketCount = 12;
        const bucketSize = Math.ceil(rangeDays / bucketCount);
        const buckets = Array.from({ length: bucketCount }, (_, i) => {
            const d = new Date(rangeStart);
            d.setDate(d.getDate() + (i * bucketSize));
            const month = d.toLocaleDateString('tr-TR', { month: 'short' });
            return {
                label: rangeDays === 365 ? `${month}` : d.toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' }),
                value: 0
            };
        });

        for (const e of events) {
            const d = new Date(String(e.date).slice(0, 10));
            const diff = Math.floor((d.getTime() - rangeStart.getTime()) / 86400000);
            const idx = Math.min(Math.floor(diff / bucketSize), bucketCount - 1);
            if (idx >= 0 && idx < bucketCount) buckets[idx].value++;
        }
        return buckets;
    }, [events, rangeDays]);

    const donutColors = ['#1A6070', '#E8A800', '#D93B2B', '#1FA35C', '#7FAAB8', '#0D3D4A'];
    const donutLegend = useMemo(() => {
        const counts = new Map<string, number>();
        for (const e of events) {
            const t = (e.eventType || '').trim() || 'Diger';
            counts.set(t, (counts.get(t) ?? 0) + 1);
        }
        const items = Array.from(counts.entries())
            .map(([type, count]) => ({ type, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 6);

        return items.map((it, idx) => ({
            label: it.type,
            value: it.count,
            color: donutColors[idx % donutColors.length],
            percent: totalEventCount ? Math.round((it.count / totalEventCount) * 100) : 0,
        }));
    }, [events, totalEventCount]);

    if (loading && !pinData) {
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress />
            </Box>
        );
    }

    // ─── Shared Sub-Components ─────────────────────────────────────────

    const renderTimeRangeToggle = () => (
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            {([['1m', '1 Ay'], ['3m', '3 Ay'], ['1y', '1 Yıl']] as const).map(([key, label]) => (
                <Box
                    key={key} component="button"
                    onClick={() => setTimeRange(key)}
                    sx={{
                        flex: 1,
                        py: isTablet ? 1.2 : 1,
                        borderRadius: reportUi.radius,
                        border: timeRange === key ? 'none' : `1.5px solid ${reportUi.border}`,
                        cursor: 'pointer',
                        fontWeight: 700,
                        fontSize: isDesktop ? 14 : 13,
                        bgcolor: timeRange === key ? reportUi.primary : reportUi.surface,
                        color: timeRange === key ? '#FFFFFF' : reportUi.textMuted,
                        boxShadow: 'none',
                        transition: 'all 0.2s',
                        '&:hover': { bgcolor: timeRange === key ? reportUi.primaryHover : reportUi.surfaceSoft },
                    }}
                >
                    {label}
                </Box>
            ))}
        </Stack>
    );

    const renderTrendChart = () => (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary', fontSize: isDesktop ? 18 : 16 }}>
                    Olay Trendi {timeRange === '1m' ? '(30 Gün)' : timeRange === '3m' ? '(3 Ay)' : '(1 Yıl)'}
                </Typography>
            </Box>
            <Paper sx={{ borderRadius: reportUi.radius, p: 2, bgcolor: reportUi.surface }} elevation={1}>
                <Box sx={{ height: isDesktop ? 180 : isMobile ? 120 : 160 }}>
                    {eventsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                            <CircularProgress size={22} />
                        </Box>
                    ) : totalEventCount === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                            Bu aralıkta olay yok
                        </Typography>
                    ) : (
                        <TrendSparkline counts={trendCounts} height={isDesktop ? 180 : isMobile ? 120 : 160} />
                    )}
                </Box>
            </Paper>
        </Box>
    );

    const renderTypesChart = () => (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary', fontSize: isDesktop ? 18 : 16 }}>
                    Olay Türleri
                </Typography>
            </Box>
            <Paper sx={{ borderRadius: reportUi.radius, p: 2, bgcolor: reportUi.surface }} elevation={1}>
                <Box sx={{ height: isDesktop ? 180 : isMobile ? 120 : 160, display: 'flex', alignItems: 'center' }}>
                    {eventsLoading ? (
                        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                                Yükleniyor…
                            </Typography>
                        </Box>
                    ) : totalEventCount === 0 ? (
                        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                                Veri yok
                            </Typography>
                        </Box>
                    ) : (
                        <Stack direction="row" spacing={2} sx={{ width: '100%', alignItems: 'center' }}>
                            <PieChart
                                total={totalEventCount}
                                slices={donutLegend.map((d) => ({
                                    label: d.label, value: d.value, color: d.color,
                                }))}
                                size={isDesktop ? 130 : isMobile ? 85 : 100}
                            />
                            <Box sx={{ flex: 1, minWidth: 0, maxHeight: '100%', overflowY: 'auto', pr: 0.5, '&::-webkit-scrollbar': { width: 4 }, '&::-webkit-scrollbar-thumb': { bgcolor: reportUi.border, borderRadius: 4 } }}>
                                {donutLegend.map((d) => (
                                    <Stack
                                        key={d.label} direction="row" justifyContent="space-between"
                                        spacing={1} sx={{ mb: isDesktop ? 0.6 : 0.3, alignItems: 'center' }}
                                    >
                                        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                                            <Box sx={{ width: isDesktop ? 8 : 6, height: isDesktop ? 8 : 6, borderRadius: '50%', bgcolor: d.color, flexShrink: 0 }} />
                                            <Typography variant="body2" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: isDesktop ? 14 : 11 }}>
                                                {d.label}
                                            </Typography>
                                        </Stack>
                                        <Typography variant="body2" sx={{ fontWeight: 800, color: reportUi.textMain, fontSize: isDesktop ? 14 : 11 }}>
                                            {d.value}
                                        </Typography>
                                    </Stack>
                                ))}
                            </Box>
                        </Stack>
                    )}
                </Box>
            </Paper>
        </Box>
    );

    const renderGeneralOverview = () => (
        <Paper
            elevation={1}
            sx={{
                borderRadius: reportUi.radius,
                p: isGeneralOverviewEditing ? (isDesktop ? 2.5 : 2) : 1.5,
                bgcolor: reportUi.surface,
                mb: 2,
                border: `1px solid ${reportUi.border}`,
            }}
        >
            <Box
                onClick={() => setIsGeneralOverviewEditing((prev) => !prev)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.5,
                    mb: 1,
                    cursor: 'pointer',
                    userSelect: 'none',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AssessmentIcon sx={{ fontSize: isDesktop ? 20 : 17, color: reportUi.primary }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: reportUi.textMain }}>
                        Genel Bakış
                    </Typography>
                </Box>
                <KeyboardArrowDownIcon
                    sx={{
                        color: reportUi.textMuted,
                        transform: isGeneralOverviewEditing ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease',
                    }}
                />
            </Box>

            {!isGeneralOverviewEditing ? (
                <Box>
                    {generalOverview?.trim() ? (
                        <Box
                            sx={{
                                maxHeight: 280,
                                overflowY: 'auto',
                                pr: 0.5,
                                '&::-webkit-scrollbar': { width: 6 },
                                '&::-webkit-scrollbar-thumb': { bgcolor: reportUi.border, borderRadius: 6 },
                            }}
                        >
                            <FormattedLlmText text={generalOverview} />
                        </Box>
                    ) : (
                        <Typography variant="body2" sx={{ color: reportUi.textMain, lineHeight: 1.5 }}>
                            Henüz bir genel bakış girilmedi. Düzenlemek için tıklayın.
                        </Typography>
                    )}
                    <Typography variant="caption" sx={{ color: reportUi.textMuted, display: 'block', mt: 0.5 }}>
                        Başlığa tıklayarak açıp düzenleyebilirsiniz
                    </Typography>
                </Box>
            ) : (
                <>
                    <TextField
                        fullWidth
                        multiline
                        minRows={isDesktop ? 5 : 4}
                        maxRows={16}
                        value={generalOverview}
                        onChange={(e) => setGeneralOverview(e.target.value)}
                        placeholder="Tesisin genel güvenlik değerlendirmesi, kritik bulgular ve öncelikli aksiyonlar..."
                        InputProps={{
                            sx: {
                                bgcolor: reportUi.surface,
                                borderRadius: reportUi.radius,
                            },
                        }}
                    />
                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" sx={{ color: reportUi.textMuted }}>
                            Raporda Genel Bakış başlığı altına eklenecektir
                        </Typography>
                        <Typography variant="caption" sx={{ color: reportUi.textMuted, fontWeight: 700 }}>
                            {generalOverview.length} karakter
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} sx={{ mt: 1.25, justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleGenerateGeneralOverview}
                            disabled={isGeneratingGeneralOverview}
                            sx={{
                                borderRadius: reportUi.radius,
                                textTransform: 'none',
                                fontWeight: 700,
                                bgcolor: reportUi.primary,
                                color: '#FFFFFF',
                                boxShadow: 'none',
                                '&:hover': { bgcolor: reportUi.primaryHover, boxShadow: 'none' },
                                '&:disabled': { bgcolor: 'action.disabledBackground', color: '#FFFFFF' },
                            }}
                        >
                            {isGeneratingGeneralOverview ? 'Üretiliyor...' : 'Umay ile Oluştur'}
                        </Button>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={async () => {
                                await handleSaveGeneralOverview();
                                setIsGeneralOverviewEditing(false);
                            }}
                            disabled={isSavingGeneralOverview || isGeneratingGeneralOverview}
                            sx={{
                                borderRadius: reportUi.radius,
                                textTransform: 'none',
                                fontWeight: 700,
                                bgcolor: reportUi.primary,
                                color: '#FFFFFF',
                                boxShadow: 'none',
                                '&:hover': { bgcolor: reportUi.primaryHover, boxShadow: 'none' },
                                '&:disabled': { bgcolor: 'action.disabledBackground', color: '#FFFFFF' },
                            }}
                        >
                            {isSavingGeneralOverview ? 'Kaydediliyor...' : 'Kaydet'}
                        </Button>
                    </Stack>
                </>
            )}
        </Paper>
    );

    const toggleEventType = (type: string) => {
        setSelectedEventTypes((prev) => {
            const next = new Set(prev);
            if (next.has(type)) next.delete(type);
            else next.add(type);
            return next;
        });
    };

    const renderRecentEventsList = () => (
        <>
            <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary', fontSize: isDesktop ? 18 : 16 }}>
                    Son Olaylar
                </Typography>
                <Chip
                    label={eventsLoading ? '…' : selectedEventTypes.size > 0 ? `${filteredEventCount} / ${totalEventCount}` : String(totalEventCount)}
                    size="small" color="error"
                    sx={{ borderRadius: reportUi.radius, fontWeight: 700 }}
                />
            </Box>
            {uniqueEventTypes.length > 1 && (
                <Box sx={{ mb: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {uniqueEventTypes.map((type) => {
                        const active = selectedEventTypes.has(type);
                        return (
                            <Chip
                                key={type}
                                label={type}
                                size="small"
                                onClick={() => toggleEventType(type)}
                                sx={{
                                    borderRadius: reportUi.radius,
                                    fontWeight: 700,
                                    fontSize: 11,
                                    height: 24,
                                    cursor: 'pointer',
                                    border: `1px solid ${active ? reportUi.primary : reportUi.border}`,
                                    bgcolor: active ? reportUi.primary : 'transparent',
                                    color: active ? '#fff' : reportUi.textMuted,
                                    '&:hover': { bgcolor: active ? reportUi.primaryHover : reportUi.surfaceSoft },
                                    transition: 'all 0.15s',
                                }}
                            />
                        );
                    })}
                    {selectedEventTypes.size > 0 && (
                        <Chip
                            label="✕"
                            size="small"
                            onClick={() => setSelectedEventTypes(new Set())}
                            sx={{
                                borderRadius: reportUi.radius,
                                fontWeight: 700, fontSize: 11, height: 24,
                                cursor: 'pointer',
                                bgcolor: 'transparent', color: reportUi.textMuted,
                                border: `1px dashed ${reportUi.border}`,
                                '&:hover': { bgcolor: 'hsl(0 70% 95%)', color: '#D93B2B' },
                                transition: 'all 0.15s',
                            }}
                        />
                    )}
                </Box>
            )}
            <Paper
                elevation={1}
                sx={{
                    borderRadius: reportUi.radius,
                    p: isDesktop ? 2 : 1.5,
                    bgcolor: reportUi.surface,
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                    maxHeight: isDesktop ? 700 : 'none',
                }}
            >
                <Box sx={{ flex: 1, overflowY: 'auto', pr: 1, '&::-webkit-scrollbar': { width: 4 }, '&::-webkit-scrollbar-thumb': { bgcolor: reportUi.border, borderRadius: 4 } }}>
                    {eventsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={22} />
                    </Box>
                ) : recentEvents.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                        Bu aralıkta olay bulunamadı.
                    </Typography>
                ) : (
                    <Stack spacing={1}>
                        {recentEvents.map((e) => {
                            const dateLabel = new Date(e.date).toLocaleDateString('tr-TR', {
                                day: '2-digit', month: 'short',
                            });
                            return (
                                <Box
                                    key={e.id}
                                    onClick={() => setExpandedEventId(expandedEventId === e.id ? null : e.id)}
                                    sx={{
                                        display: 'flex', alignItems: 'flex-start',
                                        justifyContent: 'space-between', gap: 2,
                                        px: isDesktop ? 1.5 : 1, py: 1, borderRadius: reportUi.radius,
                                        cursor: 'pointer',
                                        '&:hover': { bgcolor: reportUi.surfaceSoft },
                                        transition: 'background-color 0.15s',
                                    }}
                                >
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                            <Chip
                                                size="small" label={e.eventType}
                                                sx={{ borderRadius: reportUi.radius, bgcolor: 'hsl(198 40% 92%)', color: reportUi.textMain, fontWeight: 700, fontSize: isDesktop ? 13 : undefined }}
                                            />
                                            {e.district1 ? (
                                                <Typography variant="caption" color="text.secondary">{e.district1}</Typography>
                                            ) : null}
                                        </Stack>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                mt: 0.6, color: 'text.primary',
                                                display: expandedEventId === e.id ? 'block' : '-webkit-box',
                                                WebkitLineClamp: expandedEventId === e.id ? 'unset' : 2,
                                                WebkitBoxOrient: 'vertical', 
                                                overflow: expandedEventId === e.id ? 'visible' : 'hidden',
                                                fontSize: isDesktop ? 14 : undefined,
                                            }}
                                        >
                                            {e.description}
                                        </Typography>
                                    </Box>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap', mt: 0.6 }}>
                                        {dateLabel}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Stack>
                )}
                </Box>
                {eventsPageCount > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1.5, borderTop: `1px solid ${reportUi.surfaceSoft}`, mt: 1 }}>
                        <Pagination 
                            count={eventsPageCount} 
                            page={eventsPage} 
                            onChange={(e, v) => setEventsPage(v)} 
                            size="small" 
                            color="primary"
                            sx={{ '& .MuiPaginationItem-root': { fontWeight: 700 } }}
                        />
                    </Box>
                )}
            </Paper>
        </>
    );

    const handleOpenApproval = (fileId: string, status: ApprovalStatus) => {
        // Check if current user is the pin owner
        const pinOwnerId = pinData?.userId;
        if (currentUserId && pinOwnerId && currentUserId === pinOwnerId) {
            setOwnerWarningOpen(true);
            return;
        }
        setApprovalDialogState({ open: true, fileId, status });
    };

    const handleSubmitApproval = async (fileId: string, status: ApprovalStatus, note: string) => {
        try {
            await EventsApi.createReportApproval({
                fileAttachmentId: fileId,
                status: status,
                note: note
            });
            setApprovalDialogState((prev) => ({ ...prev, open: false }));
            // Refresh approvals
            if (id) {
                const refreshed = await EventsApi.getReportApprovalsByPin(id);
                setPdfFiles(refreshed);
            }
        } catch (error: any) {
            alert(error.response?.data?.message || error.message || 'Bir hata oluştu');
        }
    };

    const renderStatusChip = (status?: string) => {
        const s = (status || '').toLowerCase();
        let color: 'success' | 'warning' | 'error' | 'default' = 'warning';
        let icon = <HourglassEmptyIcon />;
        let label = 'Onay Bekliyor';

        if (s === 'approved') { color = 'success'; icon = <CheckCircleIcon />; label = 'Onaylandı'; }
        else if (s === 'rejected') { color = 'error'; icon = <CancelIcon />; label = 'Reddedildi'; }
        else if (s === 'pending') { color = 'warning'; icon = <HourglassEmptyIcon />; label = 'Onay Bekliyor'; }
        else if (s === 'review_needed') { color = 'error'; icon = <AssessmentIcon />; label = 'İnceleme Gerekiyor'; }

        return <Chip icon={icon} label={label} color={color} size="small" variant="outlined" sx={{ fontWeight: 600 }} />;
    };

    const renderSavedReports = () => (
        <Box sx={{ mb: 2, mt: (!isDesktop && !isMobile) ? 4 : 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary', mb: 1.5, fontSize: isDesktop ? 18 : 16 }}>
                Kaydedilen Raporlar
            </Typography>
            <Paper elevation={1} sx={{ borderRadius: reportUi.radius, p: isDesktop ? 2.5 : 1.5, bgcolor: reportUi.surface }}>
                {pdfFiles.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                        Henüz kaydedilmiş rapor bulunmamaktadır.
                    </Typography>
                ) : (
                    <>
                        {!isDesktop && (
                            <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                                <Typography variant="caption" color="text.secondary">Sıralama:</Typography>
                                <Button
                                    size="small"
                                    variant={savedReportsSortBy === 'created' ? 'contained' : 'outlined'}
                                    onClick={() => handleSavedReportsSort('created')}
                                    sx={{
                                        textTransform: 'none',
                                        fontSize: 12,
                                        minWidth: 0,
                                        py: 0.25,
                                        px: 1,
                                        boxShadow: 'none',
                                        ...(savedReportsSortBy === 'created'
                                            ? {
                                                bgcolor: '#094353',
                                                color: '#fff',
                                                borderColor: '#094353',
                                                '&:hover': { bgcolor: '#0D3D4A', color: '#fff', borderColor: '#0D3D4A', boxShadow: 'none' },
                                            }
                                            : {
                                                borderColor: reportUi.border,
                                                color: '#0D3D4A',
                                                bgcolor: reportUi.surface,
                                                '&:hover': { borderColor: '#1A6070', color: '#0D3D4A', bgcolor: reportUi.surface, boxShadow: 'none' },
                                            }),
                                    }}
                                >
                                    Oluşturulma {savedReportsSortBy === 'created' ? (savedReportsSortOrder === 'asc' ? '↑' : '↓') : ''}
                                </Button>
                                <Button
                                    size="small"
                                    variant={savedReportsSortBy === 'updated' ? 'contained' : 'outlined'}
                                    onClick={() => handleSavedReportsSort('updated')}
                                    sx={{
                                        textTransform: 'none',
                                        fontSize: 12,
                                        minWidth: 0,
                                        py: 0.25,
                                        px: 1,
                                        boxShadow: 'none',
                                        ...(savedReportsSortBy === 'updated'
                                            ? {
                                                bgcolor: '#094353',
                                                color: '#fff',
                                                borderColor: '#094353',
                                                '&:hover': { bgcolor: '#0D3D4A', color: '#fff', borderColor: '#0D3D4A', boxShadow: 'none' },
                                            }
                                            : {
                                                borderColor: reportUi.border,
                                                color: '#0D3D4A',
                                                bgcolor: reportUi.surface,
                                                '&:hover': { borderColor: '#1A6070', color: '#0D3D4A', bgcolor: reportUi.surface, boxShadow: 'none' },
                                            }),
                                    }}
                                >
                                    Güncelleme {savedReportsSortBy === 'updated' ? (savedReportsSortOrder === 'asc' ? '↑' : '↓') : ''}
                                </Button>
                            </Stack>
                        )}
                        {isDesktop ? (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Dosya Adı</TableCell>
                                            <TableCell>Versiyon</TableCell>
                                            <TableCell sortDirection={savedReportsSortBy === 'created' ? savedReportsSortOrder : false}>
                                                <TableSortLabel
                                                    active={savedReportsSortBy === 'created'}
                                                    direction={savedReportsSortOrder}
                                                    onClick={() => handleSavedReportsSort('created')}
                                                >
                                                    Oluşturulma
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell sortDirection={savedReportsSortBy === 'updated' ? savedReportsSortOrder : false}>
                                                <TableSortLabel
                                                    active={savedReportsSortBy === 'updated'}
                                                    direction={savedReportsSortOrder}
                                                    onClick={() => handleSavedReportsSort('updated')}
                                                >
                                                    Güncelleme
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell>Oluşturan</TableCell>
                                            <TableCell>Durum</TableCell>
                                            <TableCell>Onaylayanlar / Notlar</TableCell>
                                            <TableCell align="right">İşlemler</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {pagedSavedReports.map((f) => (
                                            <TableRow key={f.fileAttachmentId} hover>
                                                <TableCell sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, maxWidth: 360, verticalAlign: 'top' }}>
                                                    <PictureAsPdfIcon sx={{ color: '#D93B2B', flexShrink: 0, mt: 0.25 }} />
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontWeight: 500,
                                                            fontFamily: 'ui-monospace, "Cascadia Mono", "Segoe UI Mono", monospace',
                                                            wordBreak: 'break-all',
                                                            minWidth: 0,
                                                        }}
                                                    >
                                                        {f.fileName}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                        {reportVersionByFileId.get(f.fileAttachmentId) ?? '—'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">{formatReportCreated(f.createdDate)}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {formatReportCreated(f.updatedDate ?? new Date(getReportUpdatedMs(f)).toISOString())}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">{f.createdByFirstName} {f.createdByLastName}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    {renderStatusChip(f.overallStatus)}
                                                </TableCell>
                                                <TableCell>
                                                    <AvatarGroup max={4} sx={{ justifyContent: 'flex-end', display: 'flex' }}>
                                                        {f.approvals.map(app => (
                                                            <Tooltip key={app.id} title={`${app.userFirstName} ${app.userLastName} - ${app.statusText} (${new Date(app.approvalDate).toLocaleString('tr-TR')})${app.note ? '\nNot: ' + app.note : ''}`}>
                                                                <Avatar sx={{ bgcolor: app.status === ApprovalStatus.Approved ? '#1FA35C' : app.status === ApprovalStatus.Rejected ? '#D93B2B' : '#7FAAB8', width: 30, height: 30, fontSize: 13 }}>
                                                                    {app.userFirstName?.[0]}{app.userLastName?.[0]}
                                                                </Avatar>
                                                            </Tooltip>
                                                        ))}
                                                    </AvatarGroup>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                        <Tooltip title="Onayla">
                                                            <IconButton size="small" color="success" onClick={() => handleOpenApproval(f.fileAttachmentId, ApprovalStatus.Approved)}>
                                                                <CheckCircleIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Reddet/Not Ekle">
                                                            <IconButton size="small" color="error" onClick={() => handleOpenApproval(f.fileAttachmentId, ApprovalStatus.Rejected)}>
                                                                <CancelIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="İndir">
                                                            <IconButton size="small" color="primary" onClick={() => handleDownloadPdf(f.fileAttachmentId, f.fileName)}>
                                                                <DownloadIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Sil">
                                                            <IconButton size="small" sx={{ color: '#888', '&:hover': { color: '#D93B2B' } }} onClick={() => handleDeleteReport(f.fileAttachmentId, f.fileName)}>
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <Stack spacing={2}>
                                {pagedSavedReports.map((f) => (
                                    <Box key={f.fileAttachmentId} sx={{ p: 1.5, border: `1px solid ${reportUi.border}`, borderRadius: reportUi.radius }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <PictureAsPdfIcon sx={{ color: '#D93B2B' }} />
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: 600,
                                                    flex: 1,
                                                    wordBreak: 'break-all',
                                                    fontFamily: 'ui-monospace, "Cascadia Mono", "Segoe UI Mono", monospace',
                                                }}
                                            >
                                                {f.fileName}
                                            </Typography>
                                            {renderStatusChip(f.overallStatus)}
                                        </Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                            Versiyon: <b>{reportVersionByFileId.get(f.fileAttachmentId) ?? '—'}</b>
                                            {' · '}
                                            Oluşturulma: <b>{formatReportCreated(f.createdDate)}</b>
                                            {' · '}
                                            Güncelleme: <b>{formatReportCreated(f.updatedDate ?? new Date(getReportUpdatedMs(f)).toISOString())}</b>
                                        </Typography>
                                        {(f.createdByFirstName || f.createdByLastName) && (
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                Oluşturan: <b>{f.createdByFirstName} {f.createdByLastName}</b>
                                            </Typography>
                                        )}
                                        
                                        {f.approvals.length > 0 && (
                                            <Box sx={{ mb: 1.5 }}>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Değerlendirmeler:</Typography>
                                                <AvatarGroup max={5} sx={{ justifyContent: 'flex-start' }}>
                                                    {f.approvals.map(app => (
                                                        <Tooltip key={app.id} title={`${app.userFirstName} ${app.userLastName} - ${app.statusText} (${new Date(app.approvalDate).toLocaleString('tr-TR')})${app.note ? '\nNot: ' + app.note : ''}`}>
                                                            <Avatar sx={{ bgcolor: app.status === ApprovalStatus.Approved ? '#1FA35C' : app.status === ApprovalStatus.Rejected ? '#D93B2B' : '#7FAAB8', width: 28, height: 28, fontSize: 12 }}>
                                                                {app.userFirstName?.[0]}{app.userLastName?.[0]}
                                                            </Avatar>
                                                        </Tooltip>
                                                    ))}
                                                </AvatarGroup>
                                            </Box>
                                        )}

                                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                            <Button 
                                                size="small" 
                                                variant="contained" 
                                                startIcon={<CheckCircleIcon />} 
                                                onClick={() => handleOpenApproval(f.fileAttachmentId, ApprovalStatus.Approved)} 
                                                sx={{ 
                                                    flex: 1, textTransform: 'none', fontWeight: 600,
                                                    backgroundColor: '#1FA35C !important', color: '#fff !important',
                                                    '&:hover': { backgroundColor: '#178A4C !important' },
                                                    boxShadow: 'none',
                                                }}
                                            >
                                                Onayla
                                            </Button>
                                            <Button 
                                                size="small" 
                                                variant="contained" 
                                                startIcon={<CancelIcon />} 
                                                onClick={() => handleOpenApproval(f.fileAttachmentId, ApprovalStatus.Rejected)} 
                                                sx={{ 
                                                    flex: 1, textTransform: 'none', fontWeight: 600,
                                                    backgroundColor: '#D93B2B !important', color: '#fff !important',
                                                    '&:hover': { backgroundColor: '#B82020 !important' },
                                                    boxShadow: 'none',
                                                }}
                                            >
                                                Reddet
                                            </Button>
                                            <Button 
                                                size="small" 
                                                variant="contained" 
                                                onClick={() => handleDownloadPdf(f.fileAttachmentId, f.fileName)} 
                                                sx={{ 
                                                    minWidth: '40px', px: 0,
                                                    backgroundColor: '#094353 !important', color: '#fff !important',
                                                    '&:hover': { backgroundColor: '#0D3D4A !important' },
                                                    boxShadow: 'none',
                                                }}
                                            >
                                                <DownloadIcon fontSize="small" />
                                            </Button>
                                            <Button 
                                                size="small" 
                                                variant="contained" 
                                                onClick={() => handleDeleteReport(f.fileAttachmentId, f.fileName)} 
                                                sx={{ 
                                                    minWidth: '40px', px: 0,
                                                    backgroundColor: '#888 !important', color: '#fff !important',
                                                    '&:hover': { backgroundColor: '#D93B2B !important' },
                                                    boxShadow: 'none',
                                                }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </Button>
                                        </Stack>
                                    </Box>
                                ))}
                            </Stack>
                        )}
                        {pdfFiles.length > 0 && savedReportsPageCount > 1 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1.5, borderTop: '1px solid #EDF4F6', mt: 1 }}>
                                <Pagination
                                    count={savedReportsPageCount}
                                    page={savedReportsPage}
                                    onChange={(e, v) => setSavedReportsPage(v)}
                                    size="small"
                                    color="primary"
                                    sx={{ '& .MuiPaginationItem-root': { fontWeight: 700 } }}
                                />
                            </Box>
                        )}
                    </>
                )}
            </Paper>
            <ApprovalDialog 
                open={approvalDialogState.open}
                fileId={approvalDialogState.fileId}
                status={approvalDialogState.status}
                onClose={() => setApprovalDialogState((prev) => ({ ...prev, open: false }))}
                onSubmit={handleSubmitApproval}
            />
            <Dialog open={ownerWarningOpen} onClose={() => setOwnerWarningOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: reportUi.radius, bgcolor: reportUi.pageBg, border: `1px solid ${reportUi.border}` } }}>
                <DialogTitle sx={{ fontWeight: 700, bgcolor: reportUi.headerBg, color: '#fff' }}>İşlem Yapılamaz</DialogTitle>
                <DialogContent sx={{ bgcolor: reportUi.pageBg }}>
                    <Typography variant="body1" sx={{ mt: 1 }}>
                        Raporu yazan kişi kendi raporunu onaylayamaz veya reddedemez. Lütfen başka bir yetkili kullanıcının değerlendirmesini bekleyiniz.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button 
                        onClick={() => setOwnerWarningOpen(false)} 
                        variant="contained"
                        sx={{ 
                            fontWeight: 600,
                            backgroundColor: '#094353 !important', 
                            color: '#fff !important',
                            '&:hover': { backgroundColor: '#0D3D4A !important' },
                            boxShadow: 'none',
                        }}
                    >
                        Tamam
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={deleteDialogOpen} onClose={() => { setDeleteDialogOpen(false); setReportToDelete(null); }} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: reportUi.radius, bgcolor: reportUi.pageBg, border: `1px solid ${reportUi.border}` } }}>
                <DialogTitle sx={{ fontWeight: 700, bgcolor: reportUi.headerBg, color: '#fff' }}>Raporu Sil</DialogTitle>
                <DialogContent sx={{ bgcolor: reportUi.pageBg }}>
                    <Typography variant="body1" sx={{ mt: 1 }}>
                        <b>{reportToDelete?.fileName}</b> adlı raporu silmek istediğinize emin misiniz?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Bu işlem geri alınamaz. Silinen raporlar yalnızca süper yöneticiler tarafından görüntülenebilir.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button 
                        onClick={() => { setDeleteDialogOpen(false); setReportToDelete(null); }} 
                        variant="contained"
                        sx={{ 
                            fontWeight: 600,
                            backgroundColor: `${reportUi.surface} !important`,
                            color: `${reportUi.textMain} !important`,
                            border: `1px solid ${reportUi.border}`,
                            borderRadius: reportUi.radius,
                            '&:hover': { backgroundColor: `${reportUi.surfaceSoft} !important` },
                            minWidth: 90,
                            boxShadow: 'none',
                        }}
                    >
                        İptal
                    </Button>
                    <Button 
                        onClick={handleConfirmDeleteReport} 
                        variant="contained"
                        sx={{ 
                            fontWeight: 700,
                            minWidth: 90,
                            backgroundColor: '#D93B2B !important',
                            color: '#fff !important',
                            '&:hover': { backgroundColor: '#B82020 !important' },
                            boxShadow: 'none',
                        }}
                    >
                        Sil
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );

    const handleRestoreReport = async (fileAttachmentId: string) => {
        try {
            await EventsApi.restoreReport(fileAttachmentId);
            setDeletedReports((prev) => prev.filter((r) => r.fileAttachmentId !== fileAttachmentId));
            if (id) {
                const refreshed = await EventsApi.getReportApprovalsByPin(id);
                setPdfFiles(refreshed);
            }
        } catch (e: any) {
            alert(e?.response?.data?.message || 'Rapor geri getirilemedi.');
        }
    };

    const renderDeletedReports = () => {
        if (!isSuperadmin) return null;
        return (
            <Box sx={{ mb: 2, mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <RestoreIcon sx={{ fontSize: isDesktop ? 20 : 17, color: '#D93B2B' }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#D93B2B', fontSize: isDesktop ? 18 : 16 }}>
                        Silinmiş Raporlar
                    </Typography>
                    <Chip label="Süper Yönetici" size="small" sx={{ fontWeight: 700, fontSize: 11, bgcolor: '#FFF3E0', color: '#E65100', borderRadius: reportUi.radius }} />
                </Box>
                <Paper elevation={1} sx={{ borderRadius: reportUi.radius, p: isDesktop ? 2.5 : 1.5, bgcolor: reportUi.surface, border: '1px dashed #E0A0A0' }}>
                    {deletedReportsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={22} />
                        </Box>
                    ) : deletedReports.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                            Silinmiş rapor bulunmamaktadır.
                        </Typography>
                    ) : isDesktop ? (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Dosya Adı</TableCell>
                                        <TableCell>Oluşturulma</TableCell>
                                        <TableCell>Silinme Tarihi</TableCell>
                                        <TableCell>Silen Kişi</TableCell>
                                        <TableCell align="right">İşlem</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {deletedReports.map((dr) => (
                                        <TableRow key={dr.fileAttachmentId} sx={{ opacity: 0.75, '&:hover': { opacity: 1 } }}>
                                            <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <PictureAsPdfIcon sx={{ color: '#bbb', flexShrink: 0 }} />
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontWeight: 500,
                                                        fontFamily: 'ui-monospace, "Cascadia Mono", "Segoe UI Mono", monospace',
                                                        wordBreak: 'break-all',
                                                        textDecoration: 'line-through',
                                                        color: 'text.secondary',
                                                    }}
                                                >
                                                    {dr.fileName}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary">
                                                    {formatReportCreated(dr.createdDate)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ color: '#D93B2B', fontWeight: 600 }}>
                                                    {formatReportCreated(dr.deletedDate)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {dr.deletedByFirstName} {dr.deletedByLastName}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Tooltip title="Geri Getir">
                                                    <IconButton size="small" sx={{ color: '#1FA35C', '&:hover': { color: '#178A4C' } }} onClick={() => handleRestoreReport(dr.fileAttachmentId)}>
                                                        <RestoreIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Stack spacing={1.5}>
                            {deletedReports.map((dr) => (
                                <Box key={dr.fileAttachmentId} sx={{ p: 1.5, border: '1px solid #E0A0A0', borderRadius: reportUi.radius, opacity: 0.85 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                        <PictureAsPdfIcon sx={{ color: '#bbb', fontSize: 18 }} />
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 600,
                                                flex: 1,
                                                wordBreak: 'break-all',
                                                textDecoration: 'line-through',
                                                color: 'text.secondary',
                                                fontFamily: 'ui-monospace, "Cascadia Mono", "Segoe UI Mono", monospace',
                                            }}
                                        >
                                            {dr.fileName}
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.25 }}>
                                        Oluşturulma: <b>{formatReportCreated(dr.createdDate)}</b>
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#D93B2B', fontWeight: 600, mb: 0.25 }}>
                                        Silinme: {formatReportCreated(dr.deletedDate)}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                        Silen: {dr.deletedByFirstName} {dr.deletedByLastName}
                                    </Typography>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        startIcon={<RestoreIcon />}
                                        onClick={() => handleRestoreReport(dr.fileAttachmentId)}
                                        sx={{
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            backgroundColor: '#1FA35C !important',
                                            color: '#fff !important',
                                            '&:hover': { backgroundColor: '#178A4C !important' },
                                            boxShadow: 'none',
                                        }}
                                    >
                                        Geri Getir
                                    </Button>
                                </Box>
                            ))}
                        </Stack>
                    )}
                </Paper>
            </Box>
        );
    };

    const handleStartNewAudit = async () => {
        if (!id) return;
        setIsResettingAudit(true);
        try {
            await EventsApi.resetAuditData(id);
            setHasActiveAudit(false);
        } catch (e) {
            console.error('Denetim verileri sıfırlanırken hata:', e);
        } finally {
            setIsResettingAudit(false);
        }
        navigate(`/reports/${id}/checklist`);
    };

    const renderFooterActions = () => (
        <Box
            sx={{
                bgcolor: reportUi.pageBg,
                borderTop: `1px solid ${reportUi.border}`,
                p: 2,
                display: 'flex',
                gap: 2,
                flexShrink: 0,
                zIndex: 40,
            }}
        >
            <Button
                onClick={handleBack}
                variant="contained"
                sx={{
                    minWidth: 56,
                    minHeight: 56,
                    borderRadius: 0,
                    bgcolor: reportUi.surface,
                    color: reportUi.textMain,
                    boxShadow: 'none',
                    border: `2px solid ${reportUi.border}`,
                    '&:hover': {
                        borderColor: reportUi.textMain,
                        bgcolor: reportUi.surfaceSoft,
                        boxShadow: 'none',
                    },
                }}
            >
                <ArrowBackIcon />
            </Button>
            {hasActiveAudit ? (
                <Button
                    fullWidth variant="contained" onClick={handleOpenChecklist}
                    sx={{
                        minHeight: 56,
                        borderRadius: 0,
                        fontWeight: 700,
                        fontSize: 17,
                        bgcolor: reportUi.primary,
                        color: 'white',
                        boxShadow: 'none',
                        '&:hover': { bgcolor: reportUi.primaryHover, boxShadow: 'none' },
                    }}
                >
                    Denetime Devam Et →
                </Button>
            ) : (
                <Button
                    fullWidth variant="contained"
                    onClick={handleStartNewAudit}
                    disabled={isResettingAudit}
                    sx={{
                        minHeight: 56,
                        borderRadius: 0,
                        fontWeight: 700,
                        fontSize: 17,
                        bgcolor: reportUi.headerBg,
                        color: 'white',
                        boxShadow: 'none',
                        '&:hover': { bgcolor: reportUi.footerBg, boxShadow: 'none' },
                    }}
                >
                    {isResettingAudit ? 'Hazırlanıyor...' : '+ Yeni Denetim Başlat'}
                </Button>
            )}
        </Box>
    );

    // ─── RENDER ────────────────────────────────────────────────────────

    return (
        <>
            <Dialog
                open={editFacilityOpen}
                onClose={() => !isSavingFacility && setEditFacilityOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Tesis bilgilerini düzenle</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        {editFormError && (
                            <Typography color="error" variant="body2">
                                {editFormError}
                            </Typography>
                        )}
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Tesis adı"
                            fullWidth
                            variant="outlined"
                            required
                            value={editName}
                            onChange={(e) => {
                                if (e.target.value.length <= FACILITY_NAME_MAX_LEN) setEditName(e.target.value);
                            }}
                            inputProps={{ maxLength: FACILITY_NAME_MAX_LEN }}
                            helperText={`${editName.length}/${FACILITY_NAME_MAX_LEN} karakter`}
                            disabled={isSavingFacility}
                        />
                        <FormControl fullWidth margin="dense" disabled={isSavingFacility}>
                            <InputLabel id="edit-facility-sector-label">Sektör</InputLabel>
                            <Select
                                labelId="edit-facility-sector-label"
                                label="Sektör"
                                value={editSectorKey}
                                onChange={(e) => {
                                    const v = e.target.value as string;
                                    setEditSectorKey(v);
                                    if (v !== 'other') setEditSectorOther('');
                                }}
                                displayEmpty
                                renderValue={(v) =>
                                    v ? (
                                        FACILITY_SECTOR_LABELS[v] ?? v
                                    ) : (
                                        <Typography component="span" variant="body2" color="text.secondary">
                                            Seçin
                                        </Typography>
                                    )
                                }
                            >
                                <MenuItem value="" disabled>
                                    <em>Seçin</em>
                                </MenuItem>
                                {Object.entries(FACILITY_SECTOR_LABELS).map(([key, label]) => (
                                    <MenuItem key={key} value={key}>
                                        {label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {editSectorKey === 'other' && (
                            <TextField
                                margin="dense"
                                label="Sektör adı (diğer)"
                                fullWidth
                                variant="outlined"
                                required
                                value={editSectorOther}
                                onChange={(e) => setEditSectorOther(e.target.value)}
                                disabled={isSavingFacility}
                            />
                        )}
                        <TextField
                            margin="dense"
                            label="Tesis türü"
                            fullWidth
                            variant="outlined"
                            placeholder="Örn. üretim tesisi, AVM"
                            value={editFacilityType}
                            onChange={(e) => setEditFacilityType(e.target.value)}
                            disabled={isSavingFacility}
                        />
                        <FormControl fullWidth margin="dense" disabled={isSavingFacility}>
                            <InputLabel id="edit-facility-radius-label">Yarıçap (metre)</InputLabel>
                            <Select
                                labelId="edit-facility-radius-label"
                                label="Yarıçap (metre)"
                                value={editRadiusOption}
                                onChange={(e) => handleEditRadiusOptionChange(e.target.value as string)}
                            >
                                {PRESET_RADII.map((r) => (
                                    <MenuItem key={r} value={String(r)}>
                                        {r} m
                                    </MenuItem>
                                ))}
                                <MenuItem value="custom">Diğer (elle gir)</MenuItem>
                            </Select>
                        </FormControl>
                        {editRadiusOption === 'custom' && (
                            <TextField
                                margin="dense"
                                label="Yarıçap — özel (metre)"
                                type="number"
                                fullWidth
                                variant="outlined"
                                value={editCustomRadius}
                                onChange={(e) => setEditCustomRadius(e.target.value)}
                                inputProps={{ min: 1, step: 1 }}
                                disabled={isSavingFacility}
                            />
                        )}
                        <TextField
                            margin="dense"
                            label="Kişi sayısı"
                            type="number"
                            fullWidth
                            variant="outlined"
                            placeholder="İsteğe bağlı"
                            value={editEmployeeCount}
                            onChange={(e) => setEditEmployeeCount(e.target.value)}
                            inputProps={{ min: 0, step: 1 }}
                            disabled={isSavingFacility}
                            helperText="Boş bırakırsanız sunucudaki mevcut değer korunur."
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button
                        onClick={() => setEditFacilityOpen(false)}
                        disabled={isSavingFacility}
                        variant="contained"
                        sx={{
                            fontWeight: 600,
                            backgroundColor: '#e0e0e0 !important',
                            color: '#222 !important',
                            border: '1px solid #bbb',
                            '&:hover': { backgroundColor: '#d0d0d0 !important' },
                            boxShadow: 'none',
                        }}
                    >
                        İptal
                    </Button>
                    <Button
                        onClick={() => void handleSaveFacility()}
                        disabled={
                            isSavingFacility ||
                            !editName.trim() ||
                            !editSectorKey ||
                            (editSectorKey === 'other' && !editSectorOther.trim()) ||
                            editRadiusMeters <= 0
                        }
                        variant="contained"
                        sx={{
                            fontWeight: 700,
                            backgroundColor: '#1FA35C !important',
                            color: '#fff !important',
                            '&:hover': { backgroundColor: '#178A4C !important' },
                            boxShadow: 'none',
                        }}
                    >
                        {isSavingFacility ? 'Kaydediliyor...' : 'Kaydet'}
                    </Button>
                </DialogActions>
            </Dialog>
            <Box
                sx={{
                    width: '100%',
                    height: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    bgcolor: reportUi.pageBg,
                    color: reportUi.textMain,
                    borderRadius: '0 !important',
                }}
            >
                    {/* Header */}
                    <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 2,
                        px: isDesktop ? 4 : 2,
                        pt: isDesktop ? 1.5 : 1,
                        pb: 1,
                        flexShrink: 0,
                        bgcolor: reportUi.headerBg,
                        borderRadius: '0 !important',
                        '& .MuiBox-root, & .MuiButton-root, & .MuiIconButton-root': {
                            borderRadius: '0 !important',
                        },
                    }}>
                        <Button
                            onClick={handleBack} variant="contained" size={isDesktop ? "medium" : "small"}
                            sx={{
                                minWidth: isTablet ? 48 : 40, borderRadius: 0,
                                bgcolor: 'hsl(0 0% 100% / 0.14)', color: '#FFFFFF',
                                boxShadow: 'none',
                                border: '1px solid hsl(0 0% 100% / 0.25)',
                                '&:hover': { bgcolor: 'hsl(0 0% 100% / 0.2)', boxShadow: 'none' },
                            }}
                        >
                            <ArrowBackIcon fontSize={isDesktop ? "medium" : "small"} />
                        </Button>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="overline" sx={{ letterSpacing: 2, color: reportUi.accent, fontSize: isDesktop ? 13 : undefined }}>
                                TESİS DETAYLARI
                            </Typography>
                            <Box sx={{ mt: 0.25, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography
                                    variant={isMobile ? 'h6' : isDesktop ? 'h4' : 'h5'}
                                    sx={{ fontWeight: 800, color: '#FFFFFF', flex: '1 1 auto', minWidth: 0 }}
                                >
                                    {facilityName}
                                </Typography>
                                {canEditFacility && id && (
                                    <Tooltip title="Tesis bilgilerini düzenle">
                                        <span>
                                            <IconButton
                                                size={isDesktop ? 'medium' : 'small'}
                                                onClick={openEditFacility}
                                                aria-label="Tesis bilgilerini düzenle"
                                                sx={{
                                                    color: '#FFFFFF',
                                                    border: '1px solid hsl(0 0% 100% / 0.25)',
                                                    bgcolor: 'hsl(0 0% 100% / 0.08)',
                                                    '&:hover': { bgcolor: 'hsl(0 0% 100% / 0.16)' },
                                                }}
                                            >
                                                <EditIcon fontSize={isDesktop ? 'medium' : 'small'} />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                )}
                            </Box>
                            {pinData ? (
                                <Box
                                    sx={{
                                        mt: 0.75,
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        alignItems: 'baseline',
                                        gap: { xs: 0.75, sm: 1 },
                                        columnGap: { xs: 1, sm: 2 },
                                        rowGap: 0.5,
                                    }}
                                >
                                    <Typography variant="body2" sx={{ color: reportUi.headerSubtext, fontSize: isDesktop ? 14 : 12 }}>
                                        <Box component="span" sx={{ color: reportUi.accent, fontWeight: 600, mr: 0.5 }}>Sektör</Box>
                                        {sectorLabel}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: reportUi.headerSubtext, fontSize: isDesktop ? 14 : 12 }}>
                                        <Box component="span" sx={{ color: reportUi.accent, fontWeight: 600, mr: 0.5 }}>Tesis türü</Box>
                                        {facilityTypeLabel}
                                    </Typography>
                                    {radiusLabel && (
                                        <Typography variant="body2" sx={{ color: reportUi.headerSubtext, fontSize: isDesktop ? 14 : 12 }}>
                                            <Box component="span" sx={{ color: reportUi.accent, fontWeight: 600, mr: 0.5 }}>Yarıçap</Box>
                                            {radiusLabel}
                                        </Typography>
                                    )}
                                    <Typography variant="body2" sx={{ color: reportUi.headerSubtext, fontSize: isDesktop ? 14 : 12 }}>
                                        <Box component="span" sx={{ color: reportUi.accent, fontWeight: 600, mr: 0.5 }}>Kişi sayısı</Box>
                                        {employeeCountLabel}
                                    </Typography>
                                </Box>
                            ) : (
                                <Typography variant="body2" sx={{ mt: 0.75, color: reportUi.headerSubtext, fontSize: isDesktop ? 14 : 12 }}>
                                    Konum ve çalışan bilgileri yakında burada görünecek.
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    {/* Content area - scrollable */}
                    <Box
                        sx={{
                            bgcolor: reportUi.pageBg,
                            px: isDesktop ? 2 : isMobile ? 1.5 : 2.5,
                            pt: isDesktop ? 1.5 : isMobile ? 2 : 2.5,
                            pb: 2,
                            flex: 1,
                            overflowY: 'auto',
                            borderRadius: 0,
                        }}
                    >
                            {renderTimeRangeToggle()}

                            {isDesktop ? (
                                /* ── Desktop: 2-column layout ── */
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, alignItems: 'start' }}>
                                    {/* Sol sütun: Grafikler + Genel Bakış */}
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, gridColumn: '1', minHeight: 0 }}>
                                        {renderTrendChart()}
                                        {renderTypesChart()}
                                        {renderGeneralOverview()}
                                    </Box>

                                    {/* Sağ sütun: Son Olaylar */}
                                    <Box sx={{ gridColumn: '2', minHeight: 0, display: 'flex', flexDirection: 'column', alignSelf: 'start' }}>
                                        {renderRecentEventsList()}
                                    </Box>

                                    {/* Full width bottom row */}
                                    <Box sx={{ gridColumn: 'span 2' }}>
                                        {renderSavedReports()}
                                        {renderDeletedReports()}
                                    </Box>
                                </Box>
                            ) : (
                                /* ── Tablet / Mobile: single column ── */
                                <>
                                    <Box
                                        sx={{
                                            display: 'grid', gridTemplateColumns: '1.1fr 1fr',
                                            gap: 2, mb: 2,
                                            ...(isMobile && { gridTemplateColumns: '1fr' }),
                                        }}
                                    >
                                        {renderTrendChart()}
                                        {renderTypesChart()}
                                    </Box>

                                    {renderGeneralOverview()}
                                    {renderRecentEventsList()}
                                    {renderSavedReports()}
                                    {renderDeletedReports()}
                                </>
                            )}
                    </Box>

                    {/* Footer actions - sticky bottom */}
                    {renderFooterActions()}
            </Box>
        </>
    );
};

export default ReportPages;
